<script setup>
import { computed, onActivated, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');

const inboxes = useMapGetter('inboxes/getInboxesIncludingInactive');

// The column is nullable for records cached before the migration, so only an explicit false
// means inactive.
const isInboxActive = inbox => inbox.active !== false;

onActivated(() => {
  store.dispatch('inboxes/get');
});

// This is the one screen that still shows deactivated inboxes, since it is where they are brought
// back. They sink to the bottom so they stay out of the way until someone looks for them.
const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => {
    if (isInboxActive(a) !== isInboxActive(b)) return isInboxActive(a) ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return picoSearch(inboxesList.value, query, ['name', 'channel_type']);
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};

const setInboxActive = async (inbox, active) => {
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: inbox.id,
      formData: false,
      active,
    });
    useAlert(
      active
        ? t('INBOX_MGMT.ACTIVE_STATUS.API.ACTIVATED_MESSAGE')
        : t('INBOX_MGMT.ACTIVE_STATUS.API.DEACTIVATED_MESSAGE')
    );
  } catch (error) {
    useAlert(t('INBOX_MGMT.ACTIVE_STATUS.API.ERROR_MESSAGE'));
    // The switch already moved, so pull the real state back instead of leaving the row lying.
    store.dispatch('inboxes/get');
  }
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="inboxes"
      >
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button :label="$t('SETTINGS.INBOXES.NEW_INBOX')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredInboxesList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="inbox in filteredInboxesList"
          :key="inbox.id"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <div class="flex items-center gap-4">
            <div
              v-if="inbox.avatar_url"
              class="bg-n-alpha-3 rounded-xl size-10 ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
              :class="{ 'grayscale opacity-60': !isInboxActive(inbox) }"
            >
              <Avatar
                :src="inbox.avatar_url"
                :name="inbox.name"
                :size="24"
                rounded-full
              />
            </div>
            <div
              v-else
              class="size-10 justify-center bg-n-alpha-3 rounded-xl ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
              :class="{ 'opacity-60': !isInboxActive(inbox) }"
            >
              <ChannelIcon class="size-6 text-n-slate-10" :inbox="inbox" />
            </div>
            <div class="flex flex-col items-start gap-1">
              <div class="flex items-center gap-2">
                <span
                  class="block text-heading-3 capitalize"
                  :class="
                    isInboxActive(inbox) ? 'text-n-slate-12' : 'text-n-slate-11'
                  "
                >
                  {{ inbox.name }}
                </span>
                <span
                  v-if="!isInboxActive(inbox)"
                  class="inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-label-small bg-n-amber-3 text-n-amber-11"
                >
                  <Icon icon="i-lucide-power-off" class="size-3" />
                  {{ $t('INBOX_MGMT.ACTIVE_STATUS.INACTIVE_BADGE') }}
                </span>
              </div>
              <div class="flex items-center gap-1.5 text-body-main">
                <ChannelName
                  :channel-type="inbox.channel_type"
                  :medium="inbox.medium"
                  :voice-enabled="inbox.voice_enabled"
                  class="text-n-slate-11"
                />
                <template v-if="!isInboxActive(inbox)">
                  <span class="size-1 rounded-full bg-n-slate-8" />
                  <span class="text-n-amber-11">
                    {{ $t('INBOX_MGMT.ACTIVE_STATUS.INACTIVE_HINT') }}
                  </span>
                </template>
              </div>
            </div>
          </div>
          <div class="flex gap-3 justify-end items-center">
            <Switch
              v-if="isAdmin"
              v-tooltip.top="
                isInboxActive(inbox)
                  ? $t('INBOX_MGMT.ACTIVE_STATUS.DEACTIVATE_TOOLTIP')
                  : $t('INBOX_MGMT.ACTIVE_STATUS.ACTIVATE_TOOLTIP')
              "
              :model-value="isInboxActive(inbox)"
              class="mx-1"
              @update:model-value="value => setInboxActive(inbox, value)"
            />
            <router-link
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: inbox.id },
              }"
            >
              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                icon="i-woot-settings"
                slate
                sm
              />
            </router-link>
            <Button
              v-if="isAdmin"
              v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDelete(inbox)"
            />
          </div>
        </div>
      </div>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
